import { Candidate } from '@app/models/offer-letters/candidate.model';
import { CheckListFilter } from '@app/modules/table/classes/column-filters/check-list/check-list-filter.class';
import { Column } from '@app/modules/table/classes/column.class';
import { CellDisplay } from '@app/modules/table/enums/cell-display.enum';
import { BadgeDisplay } from '@app/modules/table/interfaces/badge-display.interface';
import { AppDatePipe } from '@app/pipes';
import { Translatable } from '@app/types/translatable.type';
import { OfferLetterStatusesDisplayOptions } from '@app/types/translatables/offer-letter-statuses-display.options';

enum OfferLetterStatusBadgeTypes {
    pending = 'warning',
    accepted = 'success',
    expired = 'danger',
}

export class OfferStatusColumn extends Column {
    title = 'offer-letter.offerStatus';
    type: CellDisplay = CellDisplay.badge;

    constructor() {
        super();

        const options = [
            {
                label: 'table.offer-status.pending',
                value: 'pending',
            },
            {
                label: 'table.offer-status.accepted',
                value: 'accepted',
            },
            {
                label: 'table.offer-status.expired',
                value: 'expired',
            },
            {
                label: 'table.offer-status.withdrawn',
                value: 'withdrawn',
            },
            {
                label: 'table.offer-status.declined',
                value: 'declined',
            },
            {
                label: 'table.offer-status.hired',
                value: 'hired',
            },
        ];

        this.filter = new CheckListFilter('status', options);
    }

    getBadgeDisplay(candidate: Candidate): BadgeDisplay | undefined {
        if (!candidate.latestOfferLetter || !this.showBadge(candidate)) {
            return;
        }

        const BadgeType = OfferLetterStatusBadgeTypes[candidate.latestOfferLetter.status];

        return {
            type: BadgeType,
            label:
                OfferLetterStatusesDisplayOptions[candidate.latestOfferLetter.status] ??
                candidate.latestOfferLetter.status,
        };
    }

    getCellText(candidate: Candidate): null | Translatable {
        if (!candidate.latestOfferLetter || this.showBadge(candidate)) {
            return;
        }
        const latestOfferLetter = candidate.latestOfferLetter;

        if (latestOfferLetter.isWithdrawn()) {
            // return `Withdrawn on ${format(latestOfferLetter.withdrawnAt, 'MMM D, YYYY')}`;
            return {
                key: 'table.offer-status.withdrawnOn',
                params: {
                    withdrawnOnDate: new AppDatePipe().transform(latestOfferLetter.withdrawnAt, 'mediumDate') ?? '',
                },
            };
        }

        if (latestOfferLetter.isDeclined()) {
            // return `Candidate Declined on ${format(latestOfferLetter.declinedAt, 'MMM D, YYYY')}`;
            return {
                key: 'table.offer-status.declinedOn',
                params: {
                    declinedOnDate: new AppDatePipe().transform(latestOfferLetter.declinedAt, 'mediumDate') ?? '',
                },
            };
        }

        if (candidate.isHired()) {
            return {
                key: 'table.offer-status.hiredOn',
                params: {
                    hiredOnDate: new AppDatePipe().transform(candidate.hiredAt, 'mediumDate') ?? '',
                },
            };
        }
    }

    /**
     * We have to show badges if an offer letter is pending, accepted, expired.
     * But, the tricky thing is a candidate could be hired even if the offer letter is still in a pending state.
     * In that state, we shouldn't show the badge. And, that's what this condition is checking.
     */
    private showBadge(candidate: Candidate): boolean {
        const latestOfferLetter = candidate.latestOfferLetter;

        if (candidate.isHired() || latestOfferLetter.isWithdrawn() || latestOfferLetter.isDeclined()) {
            return false;
        }

        return latestOfferLetter.isPending() || latestOfferLetter.isAccepted() || latestOfferLetter.isExpired();
    }
}
